import { useState } from 'react';
import { Plus, LogIn } from 'lucide-react';
import { motion } from 'framer-motion';
import { AuthDialog } from '@/components/auth/AuthDialog';
import { HowToSaveSheet } from '@/components/activation/HowToSaveSheet';
import fashionImage from '@/assets/empty-state-fashion.jpg';
import { useSocialProof } from '@/hooks/useSocialProof';

interface SavesEmptyStateProps {
  onAddItem?: () => void;
  /** Show sign-in state instead of "add item" state */
  signedOut?: boolean;
}

export function SavesEmptyState({ onAddItem, signedOut = false }: SavesEmptyStateProps) {
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [showHowToSave, setShowHowToSave] = useState(false);
  const { totalSaves } = useSocialProof();

  const handleClick = () => {
    if (signedOut) {
      setShowAuthDialog(true);
    } else {
      onAddItem?.();
    }
  };

  return (
    <div className="fixed inset-0 z-0 overflow-hidden">
      {/* Full-bleed image background */}
      <img
        src={fashionImage}
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
        style={{ objectPosition: '50% 30%' }}
      />
      
      {/* Gradient overlay for readability */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/20" />
      
      {/* Content positioned at bottom */}
      <div className="absolute inset-x-0 bottom-0 px-6 flex flex-col items-center" style={{ paddingBottom: 'calc(7rem + env(safe-area-inset-bottom))' }}>
        {/* Start here indicator */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex items-center gap-1.5 text-white/90 text-sm font-medium mb-4"
        >
          <span>{signedOut ? 'Get started' : 'Start here'}</span>
          <motion.span
            animate={{ y: [0, 4, 0] }}
            transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
          >
            ↓
          </motion.span>
        </motion.div>

        {/* Headline */}
        <motion.h2
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="text-2xl font-bold text-white mb-2 drop-shadow-lg text-center"
        >
          {signedOut ? 'Your personal want list' : 'Save your first find'}
        </motion.h2>
        
        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-white/80 mb-6 drop-shadow text-center max-w-xs"
        >
          {signedOut 
            ? 'Save anything from literally anywhere and we\'ll make it shopable'
            : 'This unlocks all the magic ✨'
          }
        </motion.p>

        {/* Save button with pulsing glow */}
        <motion.button
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.25, type: "spring", stiffness: 300, damping: 25 }}
          onClick={handleClick}
          className="flex items-center gap-3 px-6 py-3 rounded-full bg-white text-black font-medium shadow-xl active:scale-95 transition-transform"
          whileTap={{ scale: 0.95 }}
        >
          <span>{signedOut ? 'Sign in to continue' : 'Save Something'}</span>
          {signedOut ? <LogIn className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
        </motion.button>

        {/* Hint / Learn CTA */}
        {signedOut ? (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-sm text-white/60 mt-5 drop-shadow text-center"
          >
            Create a free account to get started
          </motion.p>
        ) : (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            onClick={() => setShowHowToSave(true)}
            className="text-sm text-white/70 mt-5 drop-shadow text-center underline underline-offset-4 decoration-white/30 active:text-white transition-colors"
          >
            Learn how to save your first item
          </motion.button>
        )}
      </div>

      {/* Auth Dialog */}
      {signedOut && (
        <AuthDialog open={showAuthDialog} onOpenChange={setShowAuthDialog} />
      )}

      {/* How to Save Sheet */}
      {!signedOut && (
        <HowToSaveSheet open={showHowToSave} onOpenChange={setShowHowToSave} />
      )}
    </div>
  );
}
